// src/pages/finances/InvoiceForm.jsx
import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Receipt as ReceiptIcon
} from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  alpha,
  useTheme
} from '@mui/material';
import { format } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// Import custom components
import Breadcrumbs from '../../components/core/Breadcrumbs';
import { Card } from '../../design-system/components/core';

// Import stores
import { MagicLoading } from '../../components/ui';
import useClientStore from '../../stores/clientStore';
import useInvoiceStore from '../../stores/invoiceStore';
import taxConfigService from '../../services/taxConfigService';
import { getDynamicCurrencyOptions } from '../../utils/dynamicCurrencyUtils';

// Import ExpenseSelector component
import ExpenseSelector from '../../components/expenses/ExpenseSelector';

const initialInvoiceState = {
  clientId: '',
  title: '',
  invoiceNumber: '',
  dates: {
    issueDate: format(new Date(), 'yyyy-MM-dd'),
    dueDate: format(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd') // Default due date: 30 days from now
  },
  items: [
    {
      description: '',
      quantity: 1,
      unitPrice: 0,
      amount: 0,
      itemType: 'service'
    }
  ],
  amount: {
    subtotal: 0,
    taxTotal: 0,
    discountTotal: 0,
    total: 0,
    paid: 0,
    due: 0
  },
  currency: {
    code: 'USD', // Will be updated dynamically
    symbol: '$'  // Will be updated dynamically
  },
  taxConfig: {
    taxConfigId: '',
    regionCode: 'DEFAULT',
    taxTypeCode: 'VAT'
  },
  terms: '',
  notes: '',
  status: 'draft',
  billing: {
    contactName: '',
    contactEmail: '',
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: ''
    }
  }
};

const InvoiceForm = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  
  // Get stores
  const { currentInvoice, isLoading, error, fetchInvoiceById, createInvoice, updateInvoice } = useInvoiceStore();
  const { clients, fetchClients, isLoading: isClientsLoading } = useClientStore();
  
  // Form state
  const [formData, setFormData] = useState(initialInvoiceState);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  
  // Currency state
  const [currencies, setCurrencies] = useState([]);
  const [loadingCurrencies, setLoadingCurrencies] = useState(false);
  const [expenseSelectorOpen, setExpenseSelectorOpen] = useState(false);
  const [selectedExpenseIds, setSelectedExpenseIds] = useState([]);
  
  // Tax configuration state
  const [taxConfigs, setTaxConfigs] = useState([]);
  const [loadingTaxConfigs, setLoadingTaxConfigs] = useState(false);
  const [availableTaxTypes, setAvailableTaxTypes] = useState([]);
  
  // Fetch invoice data if in edit mode
  useEffect(() => {
    if (isEditMode) {
      fetchInvoiceById(id);
    }
    
    // Fetch clients for dropdown
    fetchClients();
    
    // Fetch tax configurations
    loadTaxConfigurations();
  }, [isEditMode, id]);

  // Load currencies dynamically
  useEffect(() => {
    const loadCurrencies = async () => {
      setLoadingCurrencies(true);
      try {
        const currencyOptions = await getDynamicCurrencyOptions();
        setCurrencies(currencyOptions);
        
        // Update initial currency if currencies are loaded and current currency is default
        if (currencyOptions.length > 0 && formData.currency.code === 'USD') {
          const defaultCurrency = currencyOptions.find(c => c.value === 'USD') || currencyOptions[0];
          if (defaultCurrency) {
            setFormData(prev => ({
              ...prev,
              currency: {
                code: defaultCurrency.value,
                symbol: defaultCurrency.symbol || '$'
              }
            }));
          }
        }
      } catch (error) {
        console.error('Failed to load currencies:', error);
        setCurrencies([]);
      } finally {
        setLoadingCurrencies(false);
      }
    };
    
    loadCurrencies();
  }, []);

  // Load tax configurations
  const loadTaxConfigurations = async () => {
    try {
      setLoadingTaxConfigs(true);
      const response = await taxConfigService.getAllTaxConfigs({ isTemplate: false });
      
      // Handle both wrapped and unwrapped responses
      let configs = [];
      if (Array.isArray(response)) {
        configs = response;
      } else if (response?.data && Array.isArray(response.data)) {
        configs = response.data;
      } else if (response?.configs && Array.isArray(response.configs)) {
        configs = response.configs;
      }
      
      setTaxConfigs(configs);
    } catch (error) {
      console.error('Error loading tax configurations:', error);
    } finally {
      setLoadingTaxConfigs(false);
    }
  };
  
  // Update available tax types when tax configuration changes
  useEffect(() => {
    if (formData.taxConfig?.taxConfigId) {
      const selectedConfig = taxConfigs.find(config => config._id === formData.taxConfig.taxConfigId);
      if (selectedConfig && selectedConfig.taxTypes) {
        const types = selectedConfig.taxTypes
          .filter(type => type.isActive && type.calculationMethod !== 'progressive')
          .map(type => ({
            value: type.code,
            label: `${type.name} (${type.rate}%)`,
            rate: type.rate
          }));
        setAvailableTaxTypes(types);
        
        // If current tax type is not in the list, reset to first available
        if (types.length > 0 && !types.find(t => t.value === formData.taxConfig.taxTypeCode)) {
          setFormData(prev => ({
            ...prev,
            taxConfig: {
              ...prev.taxConfig,
              taxTypeCode: types[0].value
            }
          }));
        }
      } else {
        setAvailableTaxTypes([]);
      }
    } else {
      setAvailableTaxTypes([]);
    }
  }, [formData.taxConfig?.taxConfigId, taxConfigs]);
  
  // Set form data from current invoice
  useEffect(() => {
    if (isEditMode && currentInvoice) {
      // Format dates to YYYY-MM-DD for the input field
      const formattedInvoice = {
        ...currentInvoice,
        dates: {
          ...currentInvoice.dates,
          issueDate: currentInvoice.dates?.issueDate ? format(new Date(currentInvoice.dates.issueDate), 'yyyy-MM-dd') : '',
          dueDate: currentInvoice.dates?.dueDate ? format(new Date(currentInvoice.dates.dueDate), 'yyyy-MM-dd') : ''
        }
      };
      
      // If client is an object, extract the ID
      if (typeof formattedInvoice.clientId === 'object') {
        formattedInvoice.clientId = formattedInvoice.clientId._id;
      }
      
      // Extract expense IDs from items with expense references
      const expenseIds = formattedInvoice.items
        ?.filter(item => item.reference?.type === 'expense')
        ?.map(item => item.reference.id) || [];
      
      setSelectedExpenseIds(expenseIds);
      setFormData(formattedInvoice);
    }
  }, [isEditMode, currentInvoice]);
  
  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Handle nested fields (with dots in name)
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    
    // Clear error when field is updated
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };
  
  // Handle billing address changes
  const handleBillingAddressChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      billing: {
        ...prev.billing,
        address: {
          ...prev.billing.address,
          [name]: value
        }
      }
    }));
  };
  
  // Handle item changes
  const handleItemChange = (index, field, value) => {
    const updatedItems = [...formData.items];
    updatedItems[index] = {
      ...updatedItems[index],
      [field]: value
    };
    
    // Calculate item amount
    if (field === 'quantity' || field === 'unitPrice') {
      updatedItems[index].amount = updatedItems[index].quantity * updatedItems[index].unitPrice;
    }
    
    setFormData(prev => ({
      ...prev,
      items: updatedItems
    }));
    
    // Recalculate totals
    calculateTotals(updatedItems, formData.taxConfig?.taxConfigId);
  };
  
  // Add new item
  const handleAddItem = () => {
    const newItem = {
      description: '',
      quantity: 1,
      unitPrice: 0,
      amount: 0,
      itemType: 'service'
    };
    
    const updatedItems = [...formData.items, newItem];
    
    setFormData(prev => ({
      ...prev,
      items: updatedItems
    }));
  };
  
  // Remove item
  const handleRemoveItem = (index) => {
    const item = formData.items[index];
    
    // If it's an expense item, remove it from selected expense IDs
    if (item.itemType === 'expense' && item.reference?.id) {
      setSelectedExpenseIds(prev => prev.filter(id => id !== item.reference.id));
    }
    
    const updatedItems = formData.items.filter((_, i) => i !== index);
    
    setFormData(prev => ({
      ...prev,
      items: updatedItems
    }));
    
    // Recalculate totals
    calculateTotals(updatedItems, formData.taxConfig?.taxConfigId);
  };
  
  // Calculate totals with tax integration
  const calculateTotals = async (items, taxConfigId = null) => {
    const subtotal = items.reduce((sum, item) => sum + (item.amount || 0), 0);
    let taxTotal = 0;
    const discountTotal = 0; // Implement discount calculation if needed
    
    // Calculate tax if tax configuration is selected and subtotal > 0
    if (taxConfigId && subtotal > 0) {
      try {
        const taxCalculation = await taxConfigService.calculateTax(taxConfigId, {
          amount: subtotal,
          taxTypeCode: formData.taxConfig?.taxTypeCode || 'VAT' // Use selected tax type or default to VAT
        });
        
        if (taxCalculation?.data?.totalTax) {
          taxTotal = taxCalculation.data.totalTax;
        }
      } catch (error) {
        console.error('Error calculating tax:', error);
        // Fallback to zero tax if calculation fails
        taxTotal = 0;
      }
    }
    
    const total = subtotal + taxTotal - discountTotal;
    
    setFormData(prev => ({
      ...prev,
      amount: {
        ...prev.amount,
        subtotal,
        taxTotal,
        discountTotal,
        total,
        due: total - (prev.amount?.paid || 0)
      }
    }));
  };
  
  // Handle client selection
  const handleClientSelect = (e) => {
    const clientId = e.target.value;
    setFormData(prev => ({ ...prev, clientId }));
    
    // Clear selected expenses when client changes
    if (clientId !== formData.clientId) {
      setSelectedExpenseIds([]);
      
      // Remove expense items from the invoice
      const nonExpenseItems = formData.items.filter(item => item.itemType !== 'expense');
      setFormData(prev => ({
        ...prev,
        items: nonExpenseItems
      }));
      
      // Recalculate totals
      calculateTotals(nonExpenseItems, formData.taxConfig?.taxConfigId);
    }
    
    // If a client is selected, populate billing details
    if (clientId) {
      const selectedClient = clients.find(client => client._id === clientId);
      
      if (selectedClient) {
        setFormData(prev => ({
          ...prev,
          clientId,
          billing: {
            contactName: selectedClient.contacts && selectedClient.contacts.length > 0 
              ? selectedClient.contacts.find(c => c.isPrimary)?.name || selectedClient.contacts[0].name 
              : '',
            contactEmail: selectedClient.contacts && selectedClient.contacts.length > 0 
              ? selectedClient.contacts.find(c => c.isPrimary)?.email || selectedClient.contacts[0].email 
              : '',
            address: selectedClient.address || {
              street: '',
              city: '',
              state: '',
              zipCode: '',
              country: ''
            }
          }
        }));
      }
    }
  };
  
  // Generate invoice number
  const generateInvoiceNumber = () => {
    // Format: INV-{Year}{Month}{Day}-{Random 3 digits}
    const date = new Date();
    const year = date.getFullYear().toString().slice(-2);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const random = Math.floor(100 + Math.random() * 900);
    
    const invoiceNumber = `INV-${year}${month}${day}-${random}`;
    
    setFormData(prev => ({ ...prev, invoiceNumber }));
  };
  
  // Validate form before submission
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.clientId) {
      newErrors.clientId = 'Client is required';
    }
    
    if (!formData.invoiceNumber) {
      newErrors.invoiceNumber = 'Invoice number is required';
    }
    
    if (!formData.dates?.issueDate) {
      newErrors['dates.issueDate'] = 'Issue date is required';
    }
    
    if (!formData.dates?.dueDate) {
      newErrors['dates.dueDate'] = 'Due date is required';
    }
    
    // Check if at least one item exists and is valid
    if (!formData.items || formData.items.length === 0) {
      newErrors.items = 'At least one item is required';
    } else {
      formData.items.forEach((item, index) => {
        if (!item.description) {
          newErrors[`items[${index}].description`] = 'Description is required';
        }
        
        if (item.quantity <= 0) {
          newErrors[`items[${index}].quantity`] = 'Quantity must be greater than 0';
        }
        
        if (item.unitPrice < 0) {
          newErrors[`items[${index}].unitPrice`] = 'Unit price cannot be negative';
        }
      });
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Format currency for display
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: formData.currency?.code || 'USD'
    }).format(amount);
  };
  
  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      if (isEditMode) {
        await updateInvoice(id, formData);
        navigate(`/invoices/${id}`);
      } else {
        const newInvoice = await createInvoice(formData);
        navigate(`/invoices/${newInvoice._id}`);
      }
    } catch (error) {
      console.log('Error saving invoice:', error);
      setErrors(prev => ({ ...prev, form: error.message }));
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Handle cancel button
  const handleCancel = () => {
    // If there are changes, show confirm dialog
    setConfirmDialogOpen(true);
  };
  
  // Handle confirming cancel
  const handleConfirmCancel = () => {
    setConfirmDialogOpen(false);
    navigate(isEditMode ? `/invoices/${id}` : '/invoices');
  };
  
  return (
    <Box sx={{ p: 3 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs
        current={isEditMode ? "Edit Invoice" : "Create Invoice"}
        parentLink="/invoices"
        parentLabel="Invoices"
      />
      
      {/* Header section with back button and actions */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton 
            onClick={() => navigate('/invoices')}
            sx={{ mr: 1 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5">
            {isEditMode ? "Edit Invoice" : "Create Invoice"}
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            onClick={handleCancel}
          >
            Cancel
          </Button>
          
          <Button
            variant="contained"
            
            startIcon={<SaveIcon />}
            onClick={handleSubmit}
            loading={isSubmitting}
          >
            {isEditMode ? "Save Changes" : "Create Invoice"}
          </Button>
        </Box>
      </Box>
      
      {/* Loading indicator */}
      {(isLoading || isClientsLoading) && <MagicLoading />}
      
      {/* Error message */}
      {error && (
        <Card level={1} sx={{ mb: 3, p: 2, bgcolor: alpha(theme.palette.error.main, 0.1) }}>
          <Typography color="error">{error}</Typography>
        </Card>
      )}
      
      {/* Form errors */}
      {errors.form && (
        <Card level={1} sx={{ mb: 3, p: 2, bgcolor: alpha(theme.palette.error.main, 0.1) }}>
          <Typography color="error">{errors.form}</Typography>
        </Card>
      )}
      
      {/* Form content */}
      {!(isLoading || isClientsLoading) && (
        <Grid container spacing={3}>
          {/* Invoice details */}
          <Grid item xs={12}>
            <Card level={1}>
              <Box sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>Invoice Details</Typography>
                
                <Grid container spacing={3}>
                  {/* Client selection */}
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth error={!!errors.clientId}>
                    ;<InputLabel>Client *</InputLabel>
                      <Select
                        value={formData.clientId || ''}
                        onChange={handleClientSelect}
                        label="Client *"
                      >
                        <MenuItem value="">
                          <em>Select a client</em>
                        </MenuItem>
                        {clients.map(client => (
                          <MenuItem key={client._id} value={client._id}>
                            {client.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.clientId && (
                        <Typography color="error" variant="caption">
                          {errors.clientId}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>
                  
                  {/* Invoice number */}
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <TextField
                        label="Invoice Number *"
                        value={formData.invoiceNumber || ''}
                        onChange={handleChange}
                        name="invoiceNumber"
                        fullWidth
                        error={!!errors.invoiceNumber}
                        helperText={errors.invoiceNumber}
                      />
                      <Button 
                        variant="outlined" 
                        onClick={generateInvoiceNumber}
                        sx={{ minWidth: 120 }}
                      >
                        Generate
                      </Button>
                    </Box>
                  </Grid>
                  
                  {/* Invoice title */}
                  <Grid item xs={12}>
                    <TextField
                      label="Invoice Title"
                      value={formData.title || ''}
                      onChange={handleChange}
                      name="title"
                      fullWidth
                      placeholder="e.g., Monthly Service Fee, Project Payment, etc."
                    />
                  </Grid>
                  
                  {/* Issue date */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Issue Date *"
                      type="date"
                      value={formData.dates?.issueDate || ''}
                      onChange={handleChange}
                      name="dates.issueDate"
                      fullWidth
                      error={!!errors['dates.issueDate']}
                      helperText={errors['dates.issueDate']}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Grid>
                  
                  {/* Due date */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Due Date *"
                      type="date"
                      value={formData.dates?.dueDate || ''}
                      onChange={handleChange}
                      name="dates.dueDate"
                      fullWidth
                      error={!!errors['dates.dueDate']}
                      helperText={errors['dates.dueDate']}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Grid>
                  
                  {/* Currency */}
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Currency</InputLabel>
                      <Select
                        value={formData.currency?.code || 'USD'}
                        onChange={(e) => {
                          const selectedCurrency = currencies.find(c => c.value === e.target.value);
                          setFormData(prev => ({
                            ...prev,
                            currency: {
                              code: e.target.value,
                              symbol: selectedCurrency?.symbol || '$'
                            }
                          }));
                        }}
                        label="Currency"
                      >
                        {currencies.map((currency) => (
                          <MenuItem key={currency.value} value={currency.value}>
                            {currency.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  {/* Tax Configuration */}
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Tax Configuration</InputLabel>
                      <Select
                        value={formData.taxConfig?.taxConfigId || ''}
                        onChange={(e) => {
                          const selectedConfig = taxConfigs.find(config => config._id === e.target.value);
                          setFormData(prev => ({
                            ...prev,
                            taxConfig: {
                              ...prev.taxConfig,
                              taxConfigId: e.target.value,
                              regionCode: selectedConfig?.regionCode || 'DEFAULT',
                              taxTypeCode: prev.taxConfig?.taxTypeCode || 'VAT'
                            }
                          }));
                          // Recalculate totals with new tax configuration
                          calculateTotals(formData.items, e.target.value);
                        }}
                        label="Tax Configuration"
                        disabled={loadingTaxConfigs}
                      >
                        <MenuItem value="">
                          <em>No Tax</em>
                        </MenuItem>
                        {taxConfigs.map((config) => (
                          <MenuItem key={config._id} value={config._id}>
                            {config.regionName} ({config.countryName})
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  {/* Tax Type */}
                  {formData.taxConfig?.taxConfigId && availableTaxTypes.length > 0 && (
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel>Tax Type</InputLabel>
                        <Select
                          value={formData.taxConfig?.taxTypeCode || ''}
                          onChange={(e) => {
                            setFormData(prev => ({
                              ...prev,
                              taxConfig: {
                                ...prev.taxConfig,
                                taxTypeCode: e.target.value
                              }
                            }));
                            // Recalculate totals with new tax type
                            calculateTotals(formData.items, formData.taxConfig.taxConfigId);
                          }}
                          label="Tax Type"
                        >
                          {availableTaxTypes.map((type) => (
                            <MenuItem key={type.value} value={type.value}>
                              {type.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  )}
                  
                  {/* Status (only for edit mode) */}
                  {isEditMode && (
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel>Status</InputLabel>
                        <Select
                          value={formData.status || 'draft'}
                          onChange={handleChange}
                          name="status"
                          label="Status"
                        >
                          <MenuItem value="draft">Draft</MenuItem>
                          <MenuItem value="pending">Pending</MenuItem>
                          <MenuItem value="sent">Sent</MenuItem>
                          <MenuItem value="partial">Partially Paid</MenuItem>
                          <MenuItem value="paid">Paid</MenuItem>
                          <MenuItem value="overdue">Overdue</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  )}
                </Grid>
              </Box>
            </Card>
          </Grid>
          
          {/* Billing details */}
          <Grid item xs={12}>
            <Card level={1}>
              <Box sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>Billing Details</Typography>
                
                <Grid container spacing={3}>
                  {/* Contact name */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Contact Name"
                      value={formData.billing?.contactName || ''}
                      onChange={(e) => {
                        setFormData(prev => ({
                          ...prev,
                          billing: {
                            ...prev.billing,
                            contactName: e.target.value
                          }
                        }));
                      }}
                      fullWidth
                    />
                  </Grid>
                  
                  {/* Contact email */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Contact Email"
                      value={formData.billing?.contactEmail || ''}
                      onChange={(e) => {
                        setFormData(prev => ({
                          ...prev,
                          billing: {
                            ...prev.billing,
                            contactEmail: e.target.value
                          }
                        }));
                      }}
                      fullWidth
                    />
                  </Grid>
                  
                  {/* Street address */}
                  <Grid item xs={12}>
                    <TextField
                      label="Street Address"
                      value={formData.billing?.address?.street || ''}
                      onChange={handleBillingAddressChange}
                      name="street"
                      fullWidth
                    />
                  </Grid>
                  
                  {/* City */}
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="City"
                      value={formData.billing?.address?.city || ''}
                      onChange={handleBillingAddressChange}
                      name="city"
                      fullWidth
                    />
                  </Grid>
                  
                  {/* State */}
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="State/Province"
                      value={formData.billing?.address?.state || ''}
                      onChange={handleBillingAddressChange}
                      name="state"
                      fullWidth
                    />
                  </Grid>
                  
                  {/* Zip code */}
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Zip/Postal Code"
                      value={formData.billing?.address?.zipCode || ''}
                      onChange={handleBillingAddressChange}
                      name="zipCode"
                      fullWidth
                    />
                  </Grid>
                  
                  {/* Country */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Country"
                      value={formData.billing?.address?.country || ''}
                      onChange={handleBillingAddressChange}
                      name="country"
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </Box>
            </Card>
          </Grid>
          
          {/* Invoice items */}
          <Grid item xs={12}>
            <Card level={1}>
              <Box sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">Invoice Items</Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => setExpenseSelectorOpen(true)}
                      disabled={!formData.clientId}
                    >
                      Add Billable Expenses
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={handleAddItem}
                    >
                      Add Item
                    </Button>
                  </Box>
                </Box>
                
                {errors.items && (
                  <Typography color="error" variant="body2" sx={{ mb: 2 }}>
                    {errors.items}
                  </Typography>
                )}
                
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell width="40%">Description</TableCell>
                        <TableCell width="15%">Quantity</TableCell>
                        <TableCell width="15%">Unit Price</TableCell>
                        <TableCell width="15%">Amount</TableCell>
                        <TableCell width="15%" align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {formData.items && formData.items.map((item, index) => (
                        <TableRow key={index} sx={{
                          backgroundColor: item.itemType === 'expense' ? 'action.hover' : 'transparent'
                        }}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {item.itemType === 'expense' && (
                                <ReceiptIcon fontSize="small" color="action" />
                              )}
                              <TextField
                                value={item.description}
                                onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                                fullWidth
                                placeholder="Item description"
                                variant="standard"
                                disabled={item.itemType === 'expense'}
                                error={!!errors[`items[${index}].description`]}
                                helperText={errors[`items[${index}].description`]}
                              />
                            </Box>
                            <Box sx={{ mt: 1 }}>
                              <FormControl variant="standard" size="small" fullWidth>
                                <Select
                                  value={item.itemType || 'service'}
                                  onChange={(e) => handleItemChange(index, 'itemType', e.target.value)}
                                  displayEmpty
                                  disabled={item.itemType === 'expense'}
                                >
                                  <MenuItem value="service">Service</MenuItem>
                                  <MenuItem value="product">Product</MenuItem>
                                  <MenuItem value="subscription">Subscription</MenuItem>
                                  <MenuItem value="retainer">Retainer</MenuItem>
                                  <MenuItem value="expense">Expense</MenuItem>
                                  <MenuItem value="other">Other</MenuItem>
                                </Select>
                              </FormControl>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <TextField
                              type="number"
                              value={item.quantity}
                              onChange={(e) => handleItemChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                              variant="standard"
                              disabled={item.itemType === 'expense'}
                              InputProps={{ inputProps: { min: 1 } }}
                              error={!!errors[`items[${index}].quantity`]}
                              helperText={errors[`items[${index}].quantity`]}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              type="number"
                              value={item.unitPrice}
                              onChange={(e) => handleItemChange(index, 'unitPrice', parseFloat(e.target.value) || 0)}
                              variant="standard"
                              disabled={item.itemType === 'expense'}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    {formData.currency?.symbol || '$'}
                                  </InputAdornment>
                                ),
                                inputProps: { min: 0 }
                              }}
                              error={!!errors[`items[${index}].unitPrice`]}
                              helperText={errors[`items[${index}].unitPrice`]}
                            />
                          </TableCell>
                          <TableCell>
                            {formatCurrency(item.amount || 0)}
                          </TableCell>
                          <TableCell align="center">
                            <IconButton
                              onClick={() => handleRemoveItem(index)}
                              disabled={formData.items.length <= 1}
                              color="error"
                              size="small"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                      
                      {/* Totals */}
                      <TableRow>
                        <TableCell colSpan={3} align="right">
                          <Typography variant="subtitle2">Subtotal</Typography>
                        </TableCell>
                        <TableCell>
                          {formatCurrency(formData.amount?.subtotal || 0)}
                        </TableCell>
                        <TableCell />
                      </TableRow>
                      
                      {/* Tax */}
                      {formData.amount?.taxTotal > 0 && (
                        <TableRow>
                          <TableCell colSpan={3} align="right">
                            <Typography variant="subtitle2">Tax</Typography>
                          </TableCell>
                          <TableCell>
                            {formatCurrency(formData.amount?.taxTotal || 0)}
                          </TableCell>
                          <TableCell />
                        </TableRow>
                      )}
                      
                      {/* Discount (future feature) */}
                      {/* <TableRow>
                        <TableCell colSpan={3} align="right">
                          <Typography variant="subtitle2">Discount</Typography>
                        </TableCell>
                        <TableCell>
                          - {formatCurrency(formData.amount?.discountTotal || 0)}
                        </TableCell>
                        <TableCell />
                      </TableRow> */}
                      <TableRow>
                        <TableCell colSpan={3} align="right">
                          <Typography variant="h6">Total</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="h6">
                            {formatCurrency(formData.amount?.total || 0)}
                          </Typography>
                        </TableCell>
                        <TableCell />
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Card>
          </Grid>
          
          {/* Terms and Notes */}
          <Grid item xs={12} md={6}>
            <Card level={1} sx={{ height: '100%' }}>
              <Box sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>Terms</Typography>
                <TextField
                  value={formData.terms || ''}
                  onChange={handleChange}
                  name="terms"
                  fullWidth
                  multiline
                  rows={4}
                  placeholder="Payment terms, conditions, etc."
                />
              </Box>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Card level={1} sx={{ height: '100%' }}>
              <Box sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>Notes</Typography>
                <TextField
                  value={formData.notes || ''}
                  onChange={handleChange}
                  name="notes"
                  fullWidth
                  multiline
                  rows={4}
                  placeholder="Additional notes to the client"
                />
              </Box>
            </Card>
          </Grid>
          
          {/* Submit button */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={handleCancel}
              >
                Cancel
              </Button>
              
              <Button
                variant="contained"
                
                startIcon={<SaveIcon />}
                onClick={handleSubmit}
                loading={isSubmitting}
              >
                {isEditMode ? "Save Changes" : "Create Invoice"}
              </Button>
            </Box>
          </Grid>
        </Grid>
      )}
      
      {/* Confirmation dialog for canceling form */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
      >
        <DialogTitle>Cancel {isEditMode ? "Editing" : "Creation"}?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to cancel? All unsaved changes will be lost.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>No, Continue Editing</Button>
          <Button onClick={handleConfirmCancel} variant="contained" color="error">
            Yes, Cancel
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Expense Selector Dialog */}
      <ExpenseSelector
        open={expenseSelectorOpen}
        onClose={() => setExpenseSelectorOpen(false)}
        clientId={formData.clientId}
        existingExpenseIds={selectedExpenseIds}
        onSelect={(invoiceItems, expenseIds) => {
          // Add the expense items to the invoice
          const updatedItems = [...formData.items, ...invoiceItems];
          setFormData(prev => ({
            ...prev,
            items: updatedItems
          }));
          
          // Update the selected expense IDs
          setSelectedExpenseIds(prev => [...prev, ...expenseIds]);
          
          // Recalculate totals
          calculateTotals(updatedItems, formData.taxConfig?.taxConfigId);
          
          // Close the dialog
          setExpenseSelectorOpen(false);
        }}
      />
    </Box>
  );
};

export default InvoiceForm